/**
 * Overflow menu for a single unit row. Lazy-loads the unit's row options when the row
 * renders (i.e. when the property card is expanded), builds the menu entirely from that
 * data, and disables the menu trigger while the request is in flight.
 */
import { AccountViewListingOptionType } from '@zg-rentals/ts-rental-manager-api-client';

import { usePropertyTableUnitOptions } from '../../../hooks/queries/propertyTable';
import { usePrefetchMonthlyRent } from '../../../hooks/usePrefetchMonthlyRent';

import { buildOverflowMenuItems, OverflowMenu } from '../../../components/OverflowMenu';

import type { ListingAlias, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listingAlias: ListingAlias;
  unitNumber: string;
  address: RentalManagerAddressDto;
  isDisabled?: boolean;
  onArchiveClick?: (listingAlias: ListingAlias, address: RentalManagerAddressDto) => void;
  onEditClick?: (listingAlias: ListingAlias) => void;
  onEditRentClick?: () => void;
};

const UnitOverflowMenu = ({
  listingAlias,
  unitNumber,
  address,
  isDisabled = false,
  onArchiveClick,
  onEditClick,
  onEditRentClick,
}: Props) => {
  const { data, isFetching } = usePropertyTableUnitOptions(listingAlias);
  const prefetchMonthlyRent = usePrefetchMonthlyRent();
  const hasEditRent =
    Boolean(onEditRentClick) &&
    Boolean(data?.options?.some(({ type }) => type === AccountViewListingOptionType.EDIT_RENT));

  const items = buildOverflowMenuItems(data?.options ?? [], {
    actionOverrides: {
      ...(onArchiveClick && {
        [AccountViewListingOptionType.ARCHIVE]: () => onArchiveClick(listingAlias, address),
        [AccountViewListingOptionType.UNARCHIVE]: () => onArchiveClick(listingAlias, address),
      }),
      ...(onEditClick && {
        [AccountViewListingOptionType.EDIT_LISTING]: () => onEditClick(listingAlias),
      }),
      ...(onEditRentClick && {
        [AccountViewListingOptionType.EDIT_RENT]: onEditRentClick,
      }),
    },
  });

  return (
    <OverflowMenu
      triggerLabel={unitNumber ? `Options for ${unitNumber}` : 'Unit options'}
      items={items}
      disabled={isFetching || isDisabled}
      onOpen={hasEditRent ? () => prefetchMonthlyRent(listingAlias) : undefined}
    />
  );
};

export default UnitOverflowMenu;
